"use client";
import React from "react";
import { useSelectedLayoutSegments } from "next/navigation";
import Link from "@/components/core/localized-link";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

type BreadcrumbProps = {
  layout: string;
};

type SubCrumb = { href: string; text: string; hasId?: boolean };
type Breadcrumb = { crumb: string; crumbRef: string; subCrumbs: SubCrumb[] };

function BreadcrumbMenu({ layout }: Readonly<BreadcrumbProps>): React.JSX.Element {
  const segments = useSelectedLayoutSegments().filter((segment) => !segment.startsWith("("));

  const breadcrumbData: Record<string, Breadcrumb> = {
    account: {
      crumb: t`Account`,
      crumbRef: "account",
      subCrumbs: [
        { href: "/voucherwallet", text: t`Voucher Wallet` },
        { href: "/voucherwallet/order-voucher", text: t`Order Voucher`, hasId: true },
        { href: "/transactions", text: t`Your Transactions` },
        { href: "/orders", text: t`Order History` },
        { href: "/orders/info", text: t`Order Information` },
        { href: "/edit-account", text: t`Edit Information` },
        { href: "/password", text: t`Change Password` },
        { href: "/addresses", text: t`Address Book` },
        { href: "/addresses/add", text: t`Add Address` },
        { href: "/addresses/edit", text: t`Edit Address` },
        { href: "/account", text: t`My Account` },
        { href: "/wishlist", text: t`My Wish List` },
        { href: "/newsletter", text: t`Newsletter` },
        { href: "/offerz-coupons", text: t`Offerz Giftcard` },
        { href: "/security", text: t`Security` },
      ],
    },
    loyalty: {
      crumb: t`Offerz Plus`,
      crumbRef: "loyalty",
      subCrumbs: [
        { href: "/slotmachine", text: t`Slotmachine` },
        { href: "/scratchcard", text: t`Scratch card` },
        { href: "/rewards", text: t`Rewards` },
        { href: "/stampcards", text: t`Stamp cards` },
        { href: "/loyalty", text: t`Offerz Plus` },
      ],
    },
    cart: { crumb: t`Shopping Cart`, crumbRef: "cart", subCrumbs: [] },
    search: { crumb: t`Search`, crumbRef: "search", subCrumbs: [] },
    notifications: {
      crumb: t`Notifications`,
      crumbRef: "notifications",
      subCrumbs: [],
    },
  };

  const { crumb, crumbRef, subCrumbs } = breadcrumbData[layout] ?? {
    crumb: "",
    crumbRef: "/",
    subCrumbs: [],
  };

  const matchByHref = (segment: string) => {
    return subCrumbs.find((link) => link.href === segment);
  };

  return (
    <div className="mt-0 flex items-center overflow-x-auto pb-2.5">
      <Link href="/" data-testid="home-link" prefetch={false}>
        <p className="ease text-sm text-primary transition duration-300 hover:text-black">
          <Trans>Home</Trans>
        </p>
      </Link>

      <div className="mx-4 h-3 w-[1px] shrink-0 bg-[#00000099]" />
      <nav aria-label="breadcrumb" className="overflow-x-auto">
        <ol className="flex gap-3 whitespace-nowrap text-sm">
          <li>
            <Link
              href={`/${crumbRef}`}
              className={`transition-colors duration-300 ${segments.length === 0 ? "text-black" : "text-primary"} hover:text-black`}
              data-testid={`${crumbRef}-link`}
              prefetch={false}
            >
              {crumb}
            </Link>
          </li>

          {segments.length > 0 &&
            segments.map((_segment, index) => {
              const href = `/${segments.slice(0, index + 1).join("/")}`;
              const fullHref = `/${crumbRef}/${segments.slice(0, index + 1).join("/")}`;
              const match = matchByHref(href);
              if (!match?.text) return null;

              const isLast = match?.hasId
                ? index === segments.length - 2
                : index === segments.length - 1;

              return (
                <React.Fragment key={`${match.text}-${index}`}>
                  <li aria-hidden="true" className="text-[#00000099]">
                    ›
                  </li>
                  <li>
                    {isLast ? (
                      <span className="text-black">{match.text}</span>
                    ) : (
                      <Link
                        href={fullHref}
                        className="text-primary transition-colors duration-300 hover:text-black"
                        prefetch={false}
                      >
                        {match.text}
                      </Link>
                    )}
                  </li>
                </React.Fragment>
              );
            })}
        </ol>
      </nav>
    </div>
  );
}
export default React.memo(BreadcrumbMenu);
